import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, User, Eye, EyeOff, Sparkles, GraduationCap, Building2 } from "lucide-react";
import { safeFetchJson } from "@/utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = location.state?.role || "talent";
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (!agreeTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    setLoading(true);

    try {
      // Use safeFetchJson which handles response body reading safely (reads ONCE only)
      const parsed = await safeFetchJson(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, name, profile_type: role })
      });

      if (!parsed.ok) {
        // Extract error message from parsed response
        const errorMsg = parsed.data?.detail || parsed.data?.message || parsed.raw || `Registration failed (HTTP ${parsed.status})`;
        throw new Error(errorMsg);
      }

      const data = parsed.data;
      if (!data || !data.token) {
        throw new Error("Invalid response from server");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      
      toast.success("Account created successfully!");
      
      // Redirect to role-specific wizard for profile creation (SINGLE ROLE MODEL)
      if (role === "trainer") {
        navigate("/trainer/wizard");
      } else if (role === "enterprise") {
        navigate("/enterprise/wizard");
      } else {
        // Default: talent
        navigate("/talent/wizard");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplay = () => {
    if (role === "talent") return "Talent";
    if (role === "trainer") return "Trainer";
    if (role === "enterprise") return "Enterprise";
    return role;
  };

  const getRoleDescription = () => {
    if (role === "talent") return "Actor, Model, Singer, Dancer - Free to start";
    if (role === "trainer") return "Acting coach, Voice trainer, Dance instructor - 14-day free trial";
    if (role === "enterprise") return "Production house, Talent Agency, Casting director - 14-day free trial";
    return "";
  };

  const getRoleIcon = () => {
    if (role === "talent") return <Sparkles className="w-6 h-6 text-violet-600" />;
    if (role === "trainer") return <GraduationCap className="w-6 h-6 text-orange-600" />;
    if (role === "enterprise") return <Building2 className="w-6 h-6 text-blue-600" />;
    return <User className="w-6 h-6 text-gray-600" />;
  };

  const getRoleColor = () => {
    if (role === "talent") return "violet";
    if (role === "trainer") return "orange";
    if (role === "enterprise") return "blue";
    return "gray";
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
            <span className="font-display text-xl font-bold text-gray-900">TalentMeet</span>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            {/* Role indicator */}
            <div className={`flex items-center gap-3 p-3 bg-${getRoleColor()}-50 rounded-lg mb-6`}>
              {getRoleIcon()}
              <div>
                <p className="font-semibold text-gray-900">{getRoleDisplay()}</p>
                <p className="text-xs text-gray-600">{getRoleDescription()}</p>
              </div>
            </div>

            <div className="text-center mb-6">
              <h1 className="font-display text-2xl font-bold text-gray-900 mb-2">
                Create Account
              </h1>
              <p className="text-gray-600 text-sm">
                One-time signup with email and password
              </p>
            </div>

            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-gray-700">
                  {role === "enterprise" ? "Company / Agency Name" : "Full Name"}
                </Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={role === "enterprise" ? "Enter company name" : "Enter your full name"}
                    className="pl-10 h-12"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-700">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="pl-10 h-12"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password (min 8 chars)"
                    className="pl-10 pr-10 h-12"
                    required
                    minLength={8}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-gray-700">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="pl-10 h-12"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Checkbox 
                  id="terms" 
                  checked={agreeTerms}
                  onCheckedChange={setAgreeTerms}
                  className="mt-1"
                />
                <Label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer">
                  I agree to the <a href="#" className="text-violet-600 hover:underline">Terms of Service</a> and <a href="#" className="text-violet-600 hover:underline">Privacy Policy</a>
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full h-12 btn-primary"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? "Creating account..." : "Create Account"}
              </Button>
            </form>

            <p className="text-center text-gray-600 text-sm mt-6">
              Already have an account?{" "}
              <Link 
                to="/login" 
                state={{ role }} 
                className="text-violet-600 hover:text-violet-700 font-medium"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
